import React, { useState } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { auth, db } from '../firebase';
import { 
    signInWithEmailAndPassword, 
    createUserWithEmailAndPassword, 
    signInWithPopup, 
    GoogleAuthProvider,
    RecaptchaVerifier,
    signInWithPhoneNumber 
} from "firebase/auth";
import { setDoc, doc, getDoc } from "firebase/firestore";
import logo from '../assets/app-logo.png';
import bgForest from '../assets/bg-forest.jpg';

const MySwal = withReactContent(Swal);

const firebaseErrorToTurkish = (errorCode) => {
  switch (errorCode) {
    case 'auth/email-already-in-use': return 'Bu e-posta adresi zaten kullanılıyor. Lütfen "Giriş Yap" ekranını deneyin.';
    case 'auth/weak-password': return 'Şifreniz çok zayıf. Lütfen en az 6 karakterli daha güçlü bir şifre seçin.';
    case 'auth/invalid-email': return 'Geçersiz bir e-posta adresi girdiniz.';
    case 'auth/user-not-found': return 'Bu e-posta adresi ile kayıtlı kullanıcı bulunamadı.';
    case 'auth/wrong-password': return 'Hatalı şifre. Lütfen kontrol edip tekrar deneyin.';
    case 'auth/invalid-phone-number': return 'Girdiğiniz telefon numarası geçersiz. Başında 0 olmadan 10 haneli giriniz.';
    case 'auth/too-many-requests': return 'Çok fazla deneme yaptınız. Lütfen daha sonra tekrar deneyin.';
    case 'auth/invalid-verification-code': return 'Girdiğiniz SMS kodu hatalı veya süresi dolmuş.';
    case 'auth/operation-not-allowed': return 'Firebase panelinde bu giriş yöntemi kapalı!';
    default: return `Bir hata oluştu: ${errorCode}`;
  }
};

const Login = () => {
    const [loginMode, setLoginMode] = useState('email'); 
    const [isRegistering, setIsRegistering] = useState(false);
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneReg, setPhoneReg] = useState('');
    const [age, setAge] = useState('');
    const [gender, setGender] = useState('Belirtmek İstemiyorum');

    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState('');
    const [confirmationResult, setConfirmationResult] = useState(null);

    const handleAuth = async () => {
        if (!email || !password) {
            MySwal.fire({ icon: 'error', title: 'Eksik Bilgi', text: 'Lütfen e-posta ve şifrenizi girin.' });
            return;
        }

        if (isRegistering) {
            if (!firstName || !lastName || !phoneReg || !age) {
                MySwal.fire({ icon: 'error', title: 'Eksik Bilgi', text: 'Lütfen tüm kayıt alanlarını doldurun.' });
                return;
            }
            try {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;
                await setDoc(doc(db, "users", user.uid), {
                    firstName, lastName, phone: phoneReg, age, gender, email: user.email, kayitTarihi: new Date()
                });
                MySwal.fire({ icon: 'success', title: 'Kayıt Başarılı!', text: 'Haritaya yönlendiriliyorsunuz.', timer: 2000, showConfirmButton: false });
            } catch (error) {
                MySwal.fire({ icon: 'error', title: 'Kayıt Hatası', text: firebaseErrorToTurkish(error.code) });
            }
        } else {
            try {
                await signInWithEmailAndPassword(auth, email, password);
            } catch (error) {
                MySwal.fire({ icon: 'error', title: 'Giriş Hatası', text: firebaseErrorToTurkish(error.code) });
            }
        }
    };

    const handleGoogleLogin = async () => {
        const provider = new GoogleAuthProvider();
        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            const userRef = doc(db, "users", user.uid);
            const userDoc = await getDoc(userRef);

            if (!userDoc.exists()) {
                const displayNameParts = user.displayName ? user.displayName.split(' ') : [''];
                const googleFirstName = displayNameParts[0] || '';
                const googleLastName = displayNameParts.length > 1 ? displayNameParts.slice(1).join(' ') : '';

                await setDoc(userRef, {
                    firstName: googleFirstName, lastName: googleLastName, email: user.email, phone: "Belirtilmemiş", age: "Belirtilmemiş", gender: "Belirtilmemiş", kayitTarihi: new Date()
                });
            }
        } catch (error) {
            if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') return;
            MySwal.fire({ icon: 'error', title: 'Google Giriş Hatası', text: firebaseErrorToTurkish(error.code) });
        }
    };

    const setupRecaptcha = () => {
        if (!window.recaptchaVerifier) {
            window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
                'size': 'invisible'
            });
        }
    };

    const handleSendOtp = async () => {
        if (!phoneNumber || phoneNumber.length !== 10) {
            MySwal.fire({ icon: 'error', title: 'Hatalı Numara', text: 'Lütfen telefon numaranızı başında 0 olmadan 10 haneli girin (Örn: 5551234567).' });
            return;
        }

        MySwal.fire({ title: 'SMS Gönderiliyor...', allowOutsideClick: false, didOpen: () => MySwal.showLoading() });

        try {
            setupRecaptcha();
            const appVerifier = window.recaptchaVerifier;
            const formattedPhone = `+90${phoneNumber}`; 

            const confirmation = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
            setConfirmationResult(confirmation);
            
            MySwal.close();
            MySwal.fire({ icon: 'success', title: 'SMS Gönderildi', text: 'Lütfen telefonunuza gelen 6 haneli kodu girin.', timer: 2000, showConfirmButton: false });
        } catch (error) {
            MySwal.close();
            if(window.recaptchaVerifier) { window.recaptchaVerifier.clear(); window.recaptchaVerifier = null; }
            MySwal.fire({ icon: 'error', title: 'SMS Hatası', text: firebaseErrorToTurkish(error.code) });
        }
    };

    const handleVerifyOtp = async () => {
        if (!otp || otp.length !== 6) {
            MySwal.fire({ icon: 'warning', title: 'Eksik Kod', text: 'Lütfen 6 haneli doğrulama kodunu eksiksiz girin.' });
            return;
        }

        MySwal.fire({ title: 'Doğrulanıyor...', allowOutsideClick: false, didOpen: () => MySwal.showLoading() });

        try {
            const result = await confirmationResult.confirm(otp);
            const user = result.user;
            
            const userRef = doc(db, "users", user.uid);
            const userDoc = await getDoc(userRef);

            MySwal.close();

            if (!userDoc.exists()) {
                const { value: formValues } = await MySwal.fire({
                    title: 'Hoş Geldiniz!',
                    html: `
                        <p class="text-sm text-gray-500 mb-4 font-sans">Sisteme ilk kez giriş yapıyorsunuz. Ekiplerimizin size hitap edebilmesi için lütfen adınızı ve soyadınızı girin.</p>
                        <input id="swal-input-ad" class="swal2-input w-[80%] mx-auto" placeholder="Adınız">
                        <input id="swal-input-soyad" class="swal2-input w-[80%] mx-auto mt-2" placeholder="Soyadınız">
                    `,
                    confirmButtonText: 'Kaydet ve Başla',
                    confirmButtonColor: '#dc2626',
                    allowOutsideClick: false,
                    preConfirm: () => {
                        const ad = document.getElementById('swal-input-ad').value.trim();
                        const soyad = document.getElementById('swal-input-soyad').value.trim();
                        if (!ad || !soyad) {
                            Swal.showValidationMessage('Lütfen ad ve soyad alanlarını doldurun');
                        }
                        return { ad, soyad };
                    }
                });

                await setDoc(userRef, {
                    firstName: formValues?.ad || "İsimsiz",
                    lastName: formValues?.soyad || "Kullanıcı",
                    email: "Telefon ile Kayıt", 
                    phone: user.phoneNumber,
                    age: "Belirtilmemiş",
                    gender: "Belirtilmemiş",
                    kayitTarihi: new Date()
                });

                MySwal.fire({ icon: 'success', title: 'Harika!', text: 'Kaydınız tamamlandı, haritaya yönlendiriliyorsunuz.', timer: 2000, showConfirmButton: false });
            }
        } catch (error) {
            MySwal.close();
            MySwal.fire({ icon: 'error', title: 'Doğrulama Başarısız', text: firebaseErrorToTurkish(error.code) });
        }
    };

    return (
        <div className="flex justify-center items-center min-h-screen w-full bg-cover bg-center font-sans overflow-y-auto py-10" style={{ backgroundImage: `url(${bgForest})` }}>
            
            {/* CSS İLE RECAPTCHA LOGOSUNU GİZLİYORUZ */}
            <style>
                {`
                .grecaptcha-badge { 
                    visibility: hidden !important; 
                    display: none !important; 
                }
                `}
            </style>

            <div className="bg-white/95 backdrop-blur-sm p-10 rounded-2xl shadow-2xl w-full max-w-md flex flex-col items-center gap-4 transform transition-all hover:scale-[1.02]">
                <img src={logo} alt="Ateş Gözcüsü Logosu" className="w-24 h-24 mb-2" />
                <div id="recaptcha-container"></div>

                {loginMode === 'email' && (
                    <div className="w-full flex flex-col items-center gap-4 animate-in fade-in zoom-in duration-300">
                        <h2 className="text-3xl font-bold text-gray-800 mb-2">{isRegistering ? 'Kayıt Ol' : 'Giriş Yap'}</h2>
                        {isRegistering && (
                            <>
                                <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Adınız" className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-400" />
                                <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Soyadınız" className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-400" />
                                <input type="tel" value={phoneReg} onChange={(e) => setPhoneReg(e.target.value)} placeholder="Telefon (5xxxxxxxxx)" className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-400" />
                                <input type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="Yaşınız" className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-400" />
                                <select value={gender} onChange={(e) => setGender(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-400">
                                    <option value="Belirtmek İstemiyorum">Cinsiyet (Belirtmek İstemiyorum)</option>
                                    <option value="Erkek">Erkek</option>
                                    <option value="Kadın">Kadın</option>
                                </select>
                            </>
                        )}
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-posta Adresi" className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-400" />
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Şifre" className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-400" />
                        
                        <button onClick={handleAuth} className="w-full p-3 bg-red-600 text-white rounded-lg font-bold text-lg hover:bg-red-700 transition-colors shadow-md mt-2">
                            {isRegistering ? 'Kayıt İşlemini Tamamla' : 'Giriş Yap'}
                        </button>

                        <div className="flex items-center w-full my-2">
                            <hr className="flex-grow border-gray-300" /><span className="px-3 text-gray-400 text-sm font-bold uppercase tracking-widest">Veya</span><hr className="flex-grow border-gray-300" />
                        </div>

                        <button onClick={handleGoogleLogin} className="w-full p-3 bg-white border-2 border-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-colors shadow-sm flex items-center justify-center gap-3">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                            Google ile Devam Et
                        </button>

                        <button onClick={() => setLoginMode('phone')} className="w-full p-3 bg-slate-800 text-white rounded-lg font-bold hover:bg-black transition-colors shadow-sm flex items-center justify-center gap-3">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                            Telefon (SMS) ile Giriş Yap
                        </button>
                        
                        <p onClick={() => setIsRegistering(!isRegistering)} className="cursor-pointer text-blue-600 hover:text-blue-800 transition-colors mt-2 font-medium">
                            {isRegistering ? 'Zaten bir hesabınız var mı? Giriş Yapın.' : 'Hesabınız yok mu? Kayıt Olun.'}
                        </p>
                    </div>
                )}

                {loginMode === 'phone' && (
                    <div className="w-full flex flex-col items-center gap-4 animate-in fade-in slide-in-from-right duration-300">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2 uppercase tracking-tighter">Telefon ile Giriş</h2>
                        {!confirmationResult ? (
                            <>
                                <p className="text-sm text-gray-500 text-center font-medium mb-2">Lütfen numaranızı başında sıfır olmadan 10 haneli olarak giriniz.</p>
                                <div className="flex w-full bg-gray-50 border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-red-400">
                                    <span className="px-4 py-3 bg-gray-200 text-gray-600 font-bold border-r border-gray-300 flex items-center">🇹🇷 +90</span>
                                    <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="555 123 45 67" className="w-full p-3 bg-transparent focus:outline-none font-bold text-gray-800 tracking-wider" />
                                </div>
                                <button onClick={handleSendOtp} className="w-full p-3 bg-slate-800 text-white rounded-lg font-bold text-lg hover:bg-black transition-colors shadow-md mt-2">Doğrulama Kodu Gönder</button>
                            </>
                        ) : (
                            <>
                                <p className="text-sm text-green-600 text-center font-bold mb-2">SMS kodu telefonunuza gönderildi!</p>
                                <input type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="••••••" className="w-full p-4 border border-gray-300 rounded-lg bg-gray-50 text-center text-3xl font-black tracking-[1em] focus:outline-none focus:ring-2 focus:ring-green-400" />
                                <button onClick={handleVerifyOtp} className="w-full p-3 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 transition-colors shadow-md mt-2">Kodu Doğrula ve Giriş Yap</button>
                            </>
                        )}
                        <button onClick={() => { setLoginMode('email'); setConfirmationResult(null); setPhoneNumber(''); setOtp(''); }} className="text-sm text-gray-400 font-bold hover:text-gray-700 mt-4 flex items-center gap-1">← Geri Dön</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Login;